import { html, property } from 'lit-element';
import { nothing } from 'lit-html';
import { KatLitElement, register } from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import wrapperStyles from './panel-wrapper.lit.scss';
import { KatPanel } from './panel';

/**
 * @component {kat-panel-wrapper} KatalPanelWrapper A wrapper for a kat-panel. Controls how the panel interacts with its side-by-side content. The panel is a content container that exists outside the main page structure, like a modal. It provides a space for contextual information and/or actions that would otherwise clutter the page if included in the main body of the page. The panel will remain active until the user explicitly chooses to dismiss it by clicking the close button or completing an action.
 * @subcomponent ./panel.ts
 * @status Production
 * @slot default Main body content.
 * @slot panel Panel content.
 * @example Panel {
 *    "script": "document.querySelector('#toggle-panel').addEventListener('click', () => {
 *      const panel = document.querySelector('kat-panel');
 *
 *      if (panel.visible) {
 *        panel.close();
 *      } else {
 *        panel.open();
 *      }
 *    })",
 *    "content": "
 *      <kat-button id='toggle-panel'>Toggle Panel</kat-button>
 *      <kat-panel slot=\"panel\" header=\"Panel header\">
 *        <p>Main body content. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed doeiusmod tempor incididunt ut labore et dolore magna aliqua.</p>
 *      </kat-panel>
 *    "}
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-panel-wrapper')
export class KatPanelWrapper extends KatLitElement {
  static get styles() {
    return [baseStyles, wrapperStyles];
  }

  /** The visibility of the slotted kat-panel. Can also be controlled via the `visible` property on the panel. Defaults to false. */
  @property({ attribute: 'panel-visible' })
  panelVisible?: boolean = false;

  /**
   * The behavior of how the slotted kat-panel interacts with the content. Defaults to overlay.
   * @enum {value} overlay Panel will display over the main body content.
   * @enum {value} slide Panel will push main body content out of the way.
   */
  @property()
  variant?: 'slide' | 'overlay' = 'overlay';

  /** The size of the panel. Defaults to 'small'.
   * @enum {value} small Panel will inhabit a small portion of the screen.
   * @enum {value} large Panel will inhabit a large portion of the screen.
   */
  @property()
  size?: 'small' | 'large' = 'small';

  /** Controls whether the background mask is visible.  Only available when variant is overlay. Defaults to false. */
  @property({ attribute: 'mask-background' })
  maskBackground?: boolean = false;

  constructor() {
    super();

    this._listeners = [
      ['close', this._handlePanelEvent],
      ['open', this._handlePanelEvent],
    ];
  }

  private _handlePanelEvent({ detail }) {
    this.panelVisible = detail.visible;
  }

  // Runs once to handle panel with a visible attribute
  firstUpdated() {
    super.firstUpdated();
    const panel = this.querySelector<KatPanel>('kat-panel');
    if (panel) {
      this.panelVisible = panel.hasAttribute('visible');
    }
  }

  //TODO: Should auto-slot panel
  render() {
    const renderedMaskBackground =
      this.maskBackground && this.variant === 'overlay'
        ? html`<div class="mask-background"></div>`
        : nothing;

    return html`
      <div class="content">
        <slot></slot>
        ${renderedMaskBackground}
      </div>
      <slot name="panel"></slot>
    `;
  }
}
